import {
  createContext,
  FunctionComponent,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { CustomerDetailModel } from "../service/models/orders/customer_detail_model";
import { DiscountDataModel } from "../service/models/orders/order_discount_model";
import { OrderItemModelV2 } from "../service/models/orders/order_item_model";
import { defaultOrderVal, OrderModel, ScannedDeviceModel } from "../service/models/orders/order_model";
import {
  SubscriptionPlanModel,
  subscriptionPlanDefault,
  SubscriptionBillModel,
} from "../service/models/orders/subscription_plan_model";
import { NewTransactionModel } from "../service/models/orders/txn_model";
import ProductModel from "../service/models/product_model";
import { TransactionDataModel } from "../service/models/trannsaction_model";
import {
  CreateOrderDeviceApi,
  CreateOrderDiscountApi,
  CreateOrderExtraApi,
  CreateOrderItemApi,
  CreateOrderPaymentApi,
  CreateOrderSubscriptionApi,
  DeleteOrderDiscountApi,
  DeleteOrderExtraApi,
  DeleteOrderItemApi,
  DeleteOrderPaymentApi,
  GetOrderApi,
  GetOrderDevicesApi,
  GetOrderSubscriptionApi,
  GetOrderSubscriptionBillsApi,
  ReconcileOrderApi,
  ScannedBarCodeScanAPI,
  UpdateOrderDetailApi,
  UpdateOrderDeviceApi,
  UpdateOrderDeviceReplaceApi,
  UpdateOrderDiscountApi,
  UpdateOrderExtraApi,
  UpdateOrderItemApi,
  UpdateOrderPaymentApi,
  UpdateOrderRawMaterialAPI,
  UpdateOrderSubscriptionApi,
} from "../service/repos/order_repo";
import { GetProductsApi } from "../service/repos/products_repo";
import { OrderDetailModel } from "../ui/pages/orders/order_detail/order_detail_section";
import {
  OrderDevice,
  OrderDeviceV2,
} from "../ui/pages/orders/order_detail/order_devices/order_device_row";
import { InventoryStatus, PaymentRefType } from "../utils/enums";
import { useModal } from "./modal/modal_context";

import { ToastType, useToast } from "./toast/toast_ctx";
import { ItemMovementModel } from "../erp/inventory/models/inventory_voucher";
import { WarehouseModel } from "../erp/inventory/models/warehouse_model";
import { fetchGet, fetchPost } from "../service/api_client";
import Papa from "papaparse";

interface OrderDetailCtxModel {
  order: OrderModel;
  setOrder: React.Dispatch<React.SetStateAction<OrderModel>>;
  scannedID: string;
  setScannedID: React.Dispatch<React.SetStateAction<string>>;
  scannedDevices: ScannedDeviceModel[];
  setScannedDevices: React.Dispatch<React.SetStateAction<ScannedDeviceModel[]>>;
  reconcileOrder: () => void;
  getOrder: () => void;
  getProducts: () => void;
  products: ProductModel[];
  onProductAdded: (item: OrderItemModelV2, category: string) => void;
  onProductUpdated: (item: OrderItemModelV2, category: string) => void;
  onProductDeleted: (item: OrderItemModelV2, category: string) => void;
  refreshOrderData: () => void;
  onExtraAdded: (item: OrderItemModelV2) => void;
  onExtraUpdated: (item: OrderItemModelV2) => void;
  onExtraDeleted: (item: OrderItemModelV2) => void;
  onDiscountAdded: (item: DiscountDataModel) => void;
  onDiscountUpdated: (item: DiscountDataModel) => void;
  onDiscountDeleted: (item: DiscountDataModel) => void;
  onPaymentAdded: ({
    data,
    use_bal,
  }: {
    data: TransactionDataModel;
    use_bal: boolean;
  }) => Promise<void>;
  onPaymentUpdated: (item: TransactionDataModel) => void;
  onPaymentDeleted: (item: TransactionDataModel) => void;
  onOrderDetailUpdated: (data: OrderDetailModel) => void;
  onCustomerSelect: (data: CustomerDetailModel) => void;
  onDeleveryProfileSelect: (data: CustomerDetailModel) => void;
  useBal: boolean;
  setUseBal: React.Dispatch<React.SetStateAction<boolean>>;
  balTxnAmt: number;
  setSubscriptions: React.Dispatch<SetStateAction<SubscriptionPlanModel[]>>;
  subscriptions: SubscriptionPlanModel[];
  setSubscription: React.Dispatch<
    SetStateAction<SubscriptionPlanModel | undefined>
  >;
  subscription: SubscriptionPlanModel | undefined;
  namespaces: {
    name: string;
    namespace: string;
  }[];
  setNamespaces: React.Dispatch<
    SetStateAction<
      {
        name: string;
        namespace: string;
      }[]
    >
  >;
  getSubscription: () => Promise<void>;
  onSubmitSubscription: (data?: SubscriptionPlanModel) => Promise<void>;
  subBills: SubscriptionBillModel[];
  getDevices: () => Promise<void>;
  onCreateOrderDevices: (data: {
    device_ids: string[];
    order_id: string;
    status: string;
    org_namespace: string;
    org_name: string;
    warehouse_id?: string;
    warehouse?: WarehouseModel;
    date: Date;
    item_id: string;
    product_id: string;
  }) => Promise<boolean>;
  onScannedDeviceID: (data: { scanned_id: string }) => Promise<boolean>;
  onUpdateOrderDevice: (data: OrderDevice) => Promise<boolean>;
  onUpdateOrderDeviceReplace: (data: {
    device: OrderDeviceV2;
    return_strategy: boolean;
    replacement_strategy: boolean;
    replaceDevice?: OrderDeviceV2;
    warehouse?: WarehouseModel;
  }) => Promise<boolean>;
  onUpdateOrderRawMaterial: (data: {
    order_id: string;
    raw_material: OrderItemModelV2[];
  }) => Promise<boolean>;
  devices: OrderDeviceV2[];
  onUploadDevicesCSV: (file: File, orderId: string) => Promise<void>;
}

const defaultVal: OrderDetailCtxModel = {
  order: defaultOrderVal,
  setOrder: () => {},
  getOrder: () => {},
  scannedDevices: [],
  setScannedDevices: function (
    value: SetStateAction<ScannedDeviceModel[]>,
  ): void {},
  scannedID: "",
  setScannedID: function (value: SetStateAction<string>): void {},
  reconcileOrder: () => {},
  getProducts: () => {},
  products: [],
  onProductAdded: function (item: OrderItemModelV2, category: string): void {},
  onProductUpdated: function (
    item: OrderItemModelV2,
    category: string,
  ): void {},
  onProductDeleted: function (
    item: OrderItemModelV2,
    category: string,
  ): void {},
  onOrderDetailUpdated: function (data: OrderDetailModel): void {},
  refreshOrderData: async () => {},
  onExtraAdded: function (item: OrderItemModelV2): void {},
  onExtraUpdated: function (item: OrderItemModelV2): void {},
  onExtraDeleted: function (item: OrderItemModelV2): void {},
  onDiscountAdded: function (item: DiscountDataModel): void {},
  onDiscountUpdated: function (item: DiscountDataModel): void {},
  onDiscountDeleted: function (item: DiscountDataModel): void {},
  useBal: false,
  setUseBal: function (value: SetStateAction<boolean>): void {},
  balTxnAmt: 0,

  onPaymentUpdated: function (item: TransactionDataModel): void {},
  onPaymentDeleted: function (item: TransactionDataModel): void {},
  onCustomerSelect: function (data: CustomerDetailModel): void {},
  onDeleveryProfileSelect: function (data: CustomerDetailModel): void {},
  setSubscriptions: function (
    value: SetStateAction<SubscriptionPlanModel[]>,
  ): void {
    throw new Error("Function not implemented.");
  },
  subscriptions: [],
  setSubscription: function (
    value: SetStateAction<SubscriptionPlanModel | undefined>,
  ): void {
    throw new Error("Function not implemented.");
  },
  subscription: subscriptionPlanDefault,
  namespaces: [],
  setNamespaces: function (
    value: SetStateAction<{ name: string; namespace: string }[]>,
  ): void {
    throw new Error("Function not implemented.");
  },
  getSubscription: async () => {},
  onSubmitSubscription: async () => {},
  subBills: [],
  getDevices: function (): Promise<void> {
    throw new Error("Function not implemented.");
  },
  onCreateOrderDevices: async (data: {
    device_ids: string[];
    order_id: string;
    status: string;
    org_namespace: string;
    org_name: string;
    warehouse_id?: string;
    warehouse?: WarehouseModel;
    date: Date;
    item_id: string;
    product_id: string;
  }): Promise<boolean> => {
    return false;
  },

  onScannedDeviceID: async (data: { scanned_id: string }): Promise<boolean> =>
    false,
  onUpdateOrderDevice: function (data: OrderDevice): Promise<boolean> {
    throw new Error("Function not implemented.");
  },
  onUpdateOrderRawMaterial: function (data: {
    order_id: string;
    raw_material: OrderItemModelV2[];
  }): Promise<boolean> {
    throw new Error("Function not implemented.");
  },
  devices: [],
  onPaymentAdded: function ({
    data,
    use_bal,
  }: {
    data: TransactionDataModel;
    use_bal: boolean;
  }): Promise<void> {
    throw new Error("Function not implemented.");
  },
  onUpdateOrderDeviceReplace: function (data: {
    device: OrderDeviceV2;
    return_strategy: boolean;
    replacement_strategy: boolean;
    replaceDevice?: OrderDeviceV2;
    warehouse?: WarehouseModel;
  }): Promise<boolean> {
    throw new Error("Function not implemented.");
  },
  onUploadDevicesCSV: async (file: File, orderId: string): Promise<void> => {},
};

const context = createContext<OrderDetailCtxModel>(defaultVal);
export const useOrderDetailCtx = () => useContext(context);

interface OrderDetailContextProps {}

const OrderDetailContext: FunctionComponent<OrderDetailContextProps> = ({
  children,
}) => {
  const { order_id } = useParams<{ order_id: string }>();
  const { showToast } = useToast();
  const { loading, setLoading } = useModal();
  const [order, setOrder] = useState<OrderModel>(defaultOrderVal);
  const [scannedID, setScannedID] = useState<string>("");
  const [scannedDevices, setScannedDevices] = useState<ScannedDeviceModel[]>(
    [],
  );
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [useBal, setUseBal] = useState(false);
  const [balTxnAmt, setBalTxnAmt] = useState(0);
  const [balTxns, setBalTxns] = useState<TransactionDataModel[]>([]);
  const [balFetched, setBalFetched] = useState(false);
  const [subFetched, setSubFetched] = useState(false);
  const [deviceFetched, setDeviceFetched] = useState(false);

  const [subscriptions, setSubscriptions] = useState<SubscriptionPlanModel[]>(
    [],
  );
  const [subBills, setSubBills] = useState<SubscriptionBillModel[]>([]);
  const [namespaces, setNamespaces] = useState<
    {
      name: string;
      namespace: string;
    }[]
  >([]);

  const [subscription, setSubscription] = useState<SubscriptionPlanModel>();
  const [devices, setDevices] = useState<OrderDeviceV2[]>([]);

  const onSubmitSubscription = async (subsData?: SubscriptionPlanModel) => {
    if (!subsData) {
      subsData = subscription;
    }
    if (!subsData) return;
    subsData.order_id = order_id;
    setLoading(true);
    const res = subsData.id
      ? await UpdateOrderSubscriptionApi(subsData)
      : await CreateOrderSubscriptionApi(subsData);
    if (res.success) {
      if (Array.isArray(res.data)) {
        setSubscriptions((o) => [
          ...o.map((s) => {
            if (s.id === res.data[0].id) {
              return res.data[0];
            }
            return s;
          }),
          res.data[1],
        ]);

        setSubscription((o) => ({ ...o, ...res.data[1] }));
      } else {
        if (subsData.id) {
          setSubscriptions((o) => [
            ...o.map((s) => {
              if (s.id === res.data.id) {
                return res.data;
              }
              return s;
            }),
          ]);
        } else {
          setSubscriptions((o) => [...o, res.data]);
        }
        console.log({ res });

        setSubscription((o) => ({ ...o, ...res.data }));
      }
    }
    setLoading(false);
  };

  const getSubs = async () => {
    setLoading(true);
    const res = await GetOrderSubscriptionApi(order_id);
    if (res.success) {
      setSubscriptions(res.data);
    }
    setLoading(false);
    setSubFetched(true);
  };
  const getSubsBills = async () => {
    setLoading(true);
    const res = await GetOrderSubscriptionBillsApi(order_id);
    if (res.success) {
      setSubBills(
        res.data.map((d: SubscriptionBillModel) => {
          d.date = new Date(d.date);
          d.from_date = new Date(d.from_date);
          d.to_date = new Date(d.to_date);
          return d;
        }),
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (subscriptions.length) {
      const active = subscriptions.find((f) => f.active);
      if (active) setSubscription(active);
    }
  }, [subscriptions]);
  const getSubscription = async () => {
    if (!subFetched) {
      getSubs();
      getSubsBills();
    }
  };

  const onOrderDetailUpdated = async (data: OrderDetailModel) => {
    setLoading(true);
    data.order_id = order?.order_id;

    const res = await UpdateOrderDetailApi(data);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onCustomerSelect = async (data: CustomerDetailModel) => {
    // if (data.id === order.customer.id) {
    setOrder((old) => ({
      ...old,
      customer: data,
      customer_id: data.id!,
    }));
    return;
    // }
    // setLoading(true);
    // const res = await UpdateOrderDetailApi({
    //   customer_id: data.id,
    //   customer: data,
    //   order_id: order?.order_id,
    // });
    // if (res.success) {
    //   setOrder((old) => ({
    //     ...old,
    //     ...res.data,
    //     customer: data,
    //   }));
    //   showToast({ type: ToastType.success, text: res.message });
    // } else {
    //   showToast({ type: ToastType.error, text: res.error });
    // }
    // setLoading(false);
  };
  const onDeleveryProfileSelect = async (data: CustomerDetailModel) => {
    // if (data.id === order.customer.id) {
    setOrder((old) => ({
      ...old,
      delivery_profile: data,
      delivery_profile_id: data.id!,
    }));
    return;
    // }
    // setLoading(true);
    // const res = await UpdateOrderDetailApi({
    //   customer_id: data.id,
    //   customer: data,
    //   order_id: order?.order_id,
    // });
    // if (res.success) {
    //   setOrder((old) => ({
    //     ...old,
    //     ...res.data,
    //     customer: data,
    //   }));
    //   showToast({ type: ToastType.success, text: res.message });
    // } else {
    //   showToast({ type: ToastType.error, text: res.error });
    // }
    // setLoading(false);
  };
  const onProductAdded = async (data: OrderItemModelV2, category: string) => {
    setLoading(true);
    data.order_id = order?.order_id;

    const res = await CreateOrderItemApi(data, category);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onProductUpdated = async (data: OrderItemModelV2, category: string) => {
    setLoading(true);
    data.order_id = order?.order_id;

    const res = await UpdateOrderItemApi(data, category);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onProductDeleted = async (it: OrderItemModelV2, category: string) => {
    setLoading(true);

    const res = await DeleteOrderItemApi({
      order_id: it.order_id!,
      item_id: it.id!,
      category: category,
    });
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const onExtraAdded = async (data: OrderItemModelV2) => {
    setLoading(true);
    data.order_id = order?.order_id;

    const res = await CreateOrderExtraApi(data);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onExtraUpdated = async (data: OrderItemModelV2) => {
    setLoading(true);
    data.order_id = order.order_id!;

    const res = await UpdateOrderExtraApi(data);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onExtraDeleted = async (it: OrderItemModelV2) => {
    setLoading(true);

    const res = await DeleteOrderExtraApi({
      order_id: it.order_id!,
      item_id: it.id!,
    });
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const refreshOrderData = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/orders/${order.order_id}`;
      const response = await fetchGet(url);

      if (response.success && response.data) {
        setOrder(response.data);
      }
    } catch (error) {
      console.error("Error refreshing order:", error);
    }
  };

  const onDiscountAdded = async (data: DiscountDataModel) => {
    setLoading(true);
    data.order_id = order?.order_id;

    const res = await CreateOrderDiscountApi(data);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onDiscountUpdated = async (data: DiscountDataModel) => {
    setLoading(true);
    data.order_id = order?.order_id;

    const res = await UpdateOrderDiscountApi(data);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onDiscountDeleted = async (it: DiscountDataModel) => {
    setLoading(true);

    const res = await DeleteOrderDiscountApi({
      order_id: it.order_id!,
      item_id: it.id!,
    });
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const onPaymentAdded = async ({
    data,
    use_bal,
  }: {
    data: TransactionDataModel;
    use_bal: boolean;
  }) => {
    setLoading(true);
    if (!data.ref_id) {
      data.ref_id = order?.order_id;
      data.ref_type = PaymentRefType.order;
    }

    const form = new FormData();

    if (data.files && data.files.length > 0)
      form.append("txn_attachment", data.files[0]);
    delete data.files;
    form.append("data", JSON.stringify(data));
    form.append("use_bal", JSON.stringify(use_bal));

    const res = await CreateOrderPaymentApi({ form, order_id: order.order_id });
    if (res.success) {
      if (res.data.bill) {
        setSubBills((o) =>
          o.map((b, i) => {
            if (b.id === res.data.bill.id) {
              return res.data.bill;
            }
            return b;
          }),
        );
      } else
        setOrder((old) => ({
          ...old,
          ...res.data,
        }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onPaymentUpdated = async (data: TransactionDataModel) => {
    setLoading(true);

    data.ref_id = order?.order_id;
    data.ref_type = PaymentRefType.order;
    const res = await UpdateOrderPaymentApi(data);
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onPaymentDeleted = async (it: TransactionDataModel) => {
    setLoading(true);

    const res = await DeleteOrderPaymentApi({
      order_id: it.ref_id!,
      item_id: it.id!,
    });
    if (res.success) {
      setOrder((old) => ({
        ...old,
        ...res.data,
      }));
      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const getProducts = async () => {
    if (products.length === 0) {
      const productRes = await GetProductsApi();
      if (productRes.success) {
        setProducts(productRes.data);
      }
    }
  };

  const getDevices = async () => {
    if (deviceFetched) return;
    setLoading(true);
    console.log("Get Devices Called");
    const res = await GetOrderDevicesApi(order_id);
    if (res.success) {
      console.log("Get Devices Called for the order", res.result);
      setDevices(res.result);
      setDeviceFetched(true);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  const onCreateOrderDevices = async (data: {
    device_ids: string[];
    order_id: string;
    status: string;
    org_namespace: string;
    org_name: string;
    warehouse_id?: string;
    warehouse?: WarehouseModel;
    date: Date;
    item_id: string;
    product_id: string;
  }) => {
    if (!data.device_ids || data.device_ids.length === 0) {
      showToast({ type: ToastType.error, text: "Device id required !" });
      return false;
    }
    if (!data.status) {
      showToast({ type: ToastType.error, text: "status required !" });
      return false;
    }
    if (!data.org_namespace) {
      showToast({ type: ToastType.error, text: "Org namespace required !" });
      return false;
    }
    if (!data.date) {
      showToast({ type: ToastType.error, text: "Date required !" });
      return false;
    }

    // if (!data.item_id) {
    //   showToast({
    //     type: ToastType.error,
    //     text: "Please select item required !",
    //   });
    //   return false;
    // }

    setLoading(true);

    const warehouse_id =
      data.warehouse_id ?? data.warehouse?.id ?? data.warehouse?._id;

    const res = await CreateOrderDeviceApi({
      device_ids: data.device_ids,
      order_id: data.order_id,
      status: data.status,
      org_namespace: data.org_namespace,
      org_name: data.org_name,
      date: data.date,
      item_id: data.item_id,
      warehouse_id,
      warehouse: data.warehouse,
    });
    if (res.success) {
      setOrder((o) => ({ ...o!, ...res.data.order }));
      setDevices((o) => [...o, ...res.data.order_devices]);

      showToast({ type: ToastType.success, text: res.message });
      setLoading(false);

      return true;
    } else {
      showToast({ type: ToastType.error, text: res.error });
      setLoading(false);

      return false;
    }
  };

  const onScannedDeviceID = async (data: {
    scanned_id: string;
  }): Promise<boolean> => {
    if (
      data.scanned_id &&
      data.scanned_id.length !== 6 &&
      data.scanned_id.length !== 8
    ) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid scanned ID",
      });
      return false;
    }
    setLoading(true);
    const res = await ScannedBarCodeScanAPI(data.scanned_id);
    if (res.success) {
      setScannedDevices((prevDevices) => {
        const existingIds = new Set(prevDevices.map((d) => d.device_id));

        const newUniqueDevices = res.devices.filter(
          (d: ScannedDeviceModel) => !existingIds.has(d.device_id),
        );

        if (newUniqueDevices.length === 0) {
          showToast({
            type: ToastType.warning,
            text: "All scanned devices are already added.",
          });
          return prevDevices;
        }
        setScannedID("");
        showToast({
          type: ToastType.success,
          text: `Successfully added ${newUniqueDevices.length} new device(s).`,
        });

        return [...prevDevices, ...newUniqueDevices];
      });
      setLoading(false);
      showToast({
        type: ToastType.success,
        text: "Succesfully fetched devices",
      });
    } else {
      setLoading(false);
      showToast({ type: ToastType.error, text: res.error });
      return false;
    }
    return false;
  };

  const onUpdateOrderDevice = async (data: OrderDevice) => {
    if (!data.status) {
      showToast({ type: ToastType.error, text: "Status required !" });
      return false;
    }
    if (data.status === InventoryStatus.delivered && !data.delivery_date) {
      showToast({ type: ToastType.error, text: "Delivery date required !" });
      return false;
    }
    if (data.status === InventoryStatus.installed && !data.installation_date) {
      showToast({
        type: ToastType.error,
        text: "Installation date required !",
      });
      return false;
    }
    if (
      data.status === InventoryStatus.returnedNStocked &&
      !data.return_back_date
    ) {
      showToast({
        type: ToastType.error,
        text: "Returned date required !",
      });
      return false;
    }

    const res = await UpdateOrderDeviceApi(data);
    if (res.success) {
      setOrder((o) => ({
        ...o!,
        products: o.products.map((it, i) => {
          if (it.id === res.data.item.id) return res.data.item;
          return it;
        }),
      }));
      setOrder((o) => ({
        ...o!,
        services: o.services.map((it, i) => {
          if (it.id === res.data.item.id) return res.data.item;
          return it;
        }),
      }));
      setOrder((o) => ({
        ...o!,
        raw_materials: o.raw_materials.map((it, i) => {
          if (it.id === res.data.item.id) return res.data.item;
          return it;
        }),
      }));
      // setDevices((o) => [
      //   ...o.map((d) => {
      //     if (d.id === res.data.order_device.id) return res.data.order_device;
      //     return d;
      //   }),
      // ]);
      console.log("Response Devices", res.data.devices);
      setDevices((o) => {
        return res.data.devices;
      });

      showToast({ type: ToastType.success, text: res.message });

      return true;
    } else {
      showToast({ type: ToastType.error, text: res.error });
      return false;
    }
  };

  const onUpdateOrderDeviceReplace = async (data: {
    device: OrderDeviceV2;
    replaceDevice?: OrderDeviceV2;
  }) => {
    delete data.device.item;
    const res = await UpdateOrderDeviceReplaceApi(data);
    if (res.success) {
      // setOrder((o) => ({
      //   ...o!,
      //   items: o.items.map((it, i) => {
      //     if (it.id === res.data.item.id) return res.data.item;
      //     return it;
      //   }),
      // }));
      // setDevices((o) => [
      //   ...o.map((d) => {
      //     if (d.id === res.data.device.id) return res.data.device;
      //     return d;
      //   }),
      //   ...(res.data.replace_device ? [res.data.replace_device] : []),
      // ]);
      setOrder((o) => ({
        ...o!,
        ...res.data,
      }));
      setDevices((o) => [...res.data.devices]);

      showToast({ type: ToastType.success, text: res.message });
      return true;
    } else {
      showToast({ type: ToastType.error, text: res.error });
      return false;
    }
  };

  const onUpdateOrderRawMaterial = async (data: {
    order_id: string;
    raw_material: OrderItemModelV2[];
  }): Promise<boolean> => {
    const res = await UpdateOrderRawMaterialAPI(data);
    if (res.success) {
      setOrder((o) => ({
        ...o!,
        ...res.data.order,
      }));
      // setDevices((o) => [...res.data.devices]);

      showToast({ type: ToastType.success, text: res.message });
      return true;
    } else {
      showToast({ type: ToastType.error, text: res.error });
      return false;
    }
  };

  const getOrder = async () => {
    setLoading(true);

    if (order_id) {
      const res = await GetOrderApi(order_id);

      if (res.success) {
        // getDeliveredDevices();
        setOrder(res.data);
      } else showToast({ type: ToastType.error, text: res.error ?? "" });

      if (subFetched) {
        await getSubs();
        await getSubsBills();
      }
    }

    setLoading(false);
  };
  const reconcileOrder = async () => {
    setLoading(true);

    if (order_id) {
      const res = await ReconcileOrderApi(order_id);
      if (res.success) {
        // getDeliveredDevices();
        setOrder({ ...res.data });
        showToast({ type: ToastType.success, text: res.message ?? "" });
      } else showToast({ type: ToastType.error, text: res.error ?? "" });
    }

    setLoading(false);
  };

  const onUploadDevicesCSV = async (file: File, orderId: string) => {
    const validMimeTypes = ["text/csv", "application/vnd.ms-excel"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!validMimeTypes.includes(file.type) && fileExtension !== "csv") {
      showToast({ type: ToastType.error, text: "Only CSV files are allowed." });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results) {
        const parsedData = results.data;
        console.log("Parsed CSV Data:", parsedData);

        const upload_url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/orders/${orderId}/devices/upload-devices`;

        try {
          const res = await fetchPost(upload_url, { devices: parsedData });

          if (res.success) {
            const normalizedOrderDevices = res.results.flatMap(
              (result: any) => result.data.order_devices,
            );

            setDevices((prev) => [...prev, ...normalizedOrderDevices]);

            showToast({ type: ToastType.success, text: res.message });
          } else {
            showToast({ type: ToastType.error, text: res.error });
          }
        } catch (err) {
          showToast({
            type: ToastType.error,
            text: "File upload failed. " + err,
          });
          console.error(err);
        }
      },
      error: function (err) {
        showToast({ type: ToastType.error, text: "Failed to parse CSV." });
        console.error("PapaParse Error:", err);
      },
    });
  };

  useEffect(() => {
    getOrder();
  }, []);

  useEffect(() => {
    console.log("Scanned ID", scannedID);
  }, [scannedID]);

  const value: OrderDetailCtxModel = {
    order,
    setOrder,
    scannedDevices,
    setScannedDevices,
    scannedID,
    setScannedID,
    reconcileOrder,
    getOrder,
    getProducts,
    products,
    onProductAdded,
    onProductUpdated,
    onProductDeleted,
    onOrderDetailUpdated,
    refreshOrderData,
    onExtraAdded,
    onExtraDeleted,
    onExtraUpdated,
    onDiscountAdded,
    onDiscountUpdated,
    onDiscountDeleted,
    useBal,
    setUseBal,
    balTxnAmt,
    onPaymentAdded,
    onPaymentDeleted,
    onPaymentUpdated,
    onCustomerSelect,
    onDeleveryProfileSelect,
    setSubscriptions,
    subscriptions,
    namespaces,
    setNamespaces,
    subscription,
    setSubscription,
    onSubmitSubscription,
    getSubscription,
    subBills,
    getDevices,
    onCreateOrderDevices,
    onScannedDeviceID,
    onUpdateOrderDevice,
    onUpdateOrderDeviceReplace,
    onUpdateOrderRawMaterial,
    devices,
    onUploadDevicesCSV,
  };
  return <context.Provider value={value}>{children ?? <></>}</context.Provider>;
};

export default OrderDetailContext;
